import { useEffect, useMemo } from 'react';
import {
  useCreateAssignRoleplay,
  useCreateCustomRoleplay,
  useCreateSelfAssignRoleplay,
  useGetListRoleplaysQuery,
} from 'features/roleplay/Roleplay.queries';
import AssignRealPlayDataHelper from 'features/realplays/AssignRealPlayData.helper';
import { useGetFF } from 'hooks/useGetFF';
import useGetPersonasListQuery from 'features/realplays/services/queries/useGetPersonasList';
import useGetAttitudeListQuery from 'features/realplays/services/queries/useGetAttitudeList';
import useAssignRealPlayMutation from 'features/realplays/services/mutations/useAssignRealPlay';
import useSelfAssignRealPlayMutation from 'features/realplays/services/mutations/useSelfAssignRealPlay';
import { useRoleplayStore } from 'features/roleplay/Roleplay.store';
import DateTime from 'infrastructure/Datetime';

const AssignRolePlayLogic = ({
  active = false,
  handleClose = () => null,
  managerOption = false,
  version = false,
}) => {
  const { isEnabled: scenariosDescriptions } = useGetFF({ flag_id: 'description_scenarios' });

  const { isFetching: isLoadingCoach } = useGetPersonasListQuery({ active });
  const { isFetching: isLoadingAttitudes } = useGetAttitudeListQuery({ active });
  const { data: roleplaysList = [], isLoading } = useGetListRoleplaysQuery();

  /* Handlers normal roleplays */
  const {
    formDataAssignRoleplay,
    scenariosList,
    formDataCreateRoleplay,
    setFormDataAssignRoleplay,
    setScenariosList,
    setFormDataCreateRoleplay,
    setNameError,
    setDescriptionError,
    setCriteriaError,
    setScenariosErrors,
  } = useRoleplayStore();

  /* Handlers interactive roleplays */
  const { isRep, personas_ai, attitudes, deadline, users, context, reset } =
    AssignRealPlayDataHelper();
  const { title: role = '', industry = '', customer_situation: situation = '' } = context || {};

  /* Normal roleplays mutations */
  const { mutate: assignRoleplay, isLoading: loadingAdmin } = useCreateAssignRoleplay();
  const { mutate: selfAssignRoleplay, isLoading: loadingRep } = useCreateSelfAssignRoleplay();
  const { mutate: createCustomRoleplay, isLoading: loadingCustom } = useCreateCustomRoleplay();

  /*Interactive roleplays mutations*/
  const { mutate: newAssignRoleplay, isLoading: newLoadingAdmin } = useAssignRealPlayMutation();
  const { mutate: newSelfAssignRoleplay, isLoading: newLoadingRep } =
    useSelfAssignRealPlayMutation();

  const firstRoleplay = useMemo(() => {
    if (!roleplaysList?.length > 0) return {};
    return {
      label: roleplaysList[0]?.name,
      value: roleplaysList[0]?.uuid,
      description: roleplaysList[0]?.description,
      scenarios: roleplaysList[0]?.scenarios,
      rating_criteria: roleplaysList[0]?.rating_criteria,
      isSelected: true,
    };
  }, [roleplaysList]);

  useEffect(() => {
    if (!Object.keys(firstRoleplay).length > 0 || formDataAssignRoleplay?.roleplay) return;
    setFormDataAssignRoleplay({
      roleplay: firstRoleplay,
      ...formDataAssignRoleplay,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRoleplay]);

  const enable = useMemo(() => {
    if (isRep) {
      if (managerOption) {
        return !loadingRep;
      } else {
        if (!formDataAssignRoleplay?.users || formDataAssignRoleplay.users?.length < 1)
          return false;
        return !loadingRep;
      }
    } else {
      if (!formDataAssignRoleplay?.users || formDataAssignRoleplay?.users?.length < 1) return false;
      return !loadingAdmin || !loadingCustom;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDataAssignRoleplay.users, loadingAdmin, loadingRep, loadingCustom]);

  const isValid = () => {
    const nameError = !formDataCreateRoleplay?.name?.trim();
    const descError = !formDataCreateRoleplay?.description?.trim();
    const criteriaError = !formDataCreateRoleplay?.rating_criterials?.some((r) => r?.name?.trim());
    const scenariosErrors = scenariosDescriptions
      ? formDataCreateRoleplay?.scenarios?.map((item) =>
          item?.name === '' && item?.description === '' ? false : !item.description.trim()
        )
      : [false];

    setNameError(nameError);
    setDescriptionError(descError);
    setCriteriaError(criteriaError);
    setScenariosErrors(scenariosErrors);
    const hasScenariosErrors = scenariosErrors.includes(true);

    if (nameError || descError || criteriaError || hasScenariosErrors) return false;
    return true;
  };

  const handleAssign = () => {
    const scenarios = scenariosList.map((scenario) => {
      if (scenario.isSelected) return scenario.uuid;
      else return '';
    });
    if (isRep) {
      const data = managerOption
        ? {
            roleplay_uuid: formDataAssignRoleplay?.roleplay.value,
            scenarios_uuid: scenarios.filter((uuid) => uuid !== ''),
            deadline: formDataAssignRoleplay?.deadline,
          }
        : {
            roleplay_uuid: formDataAssignRoleplay?.roleplay.value,
            scenarios_uuid: scenarios.filter((uuid) => uuid !== ''),
            admin_uuid: formDataAssignRoleplay?.users.value,
            deadline: formDataAssignRoleplay?.deadline,
          };
      selfAssignRoleplay(
        { roleplay: data, version },
        {
          onSuccess: () => {
            setFormDataAssignRoleplay({
              scenarios: [],
              deadline: DateTime.formatDate(new Date()) + ' 00:00:00',
              roleplay: firstRoleplay,
            });
            onClose();
          },
        }
      );
    } else {
      if (formDataAssignRoleplay?.roleplay.value !== -1) {
        const users_uuid = formDataAssignRoleplay?.users.map((user) => {
          return user.value;
        });
        const data = {
          roleplay_uuid: formDataAssignRoleplay?.roleplay.value,
          scenarios_uuid: scenarios.filter((uuid) => uuid !== ''),
          users_uuid: users_uuid,
          deadline: formDataAssignRoleplay?.deadline,
        };
        assignRoleplay(
          { roleplay: data, version },
          {
            onSuccess: () => {
              setFormDataAssignRoleplay({
                scenarios: [],
                deadline: DateTime.formatDate(new Date()) + ' 00:00:00',
                roleplay: firstRoleplay,
              });
              onClose();
            },
          }
        );
      } else {
        if (!isValid()) return;
        let sales_rep_uuid_ = [];
        if (formDataAssignRoleplay?.users)
          sales_rep_uuid_ = formDataAssignRoleplay?.users?.map((user) => {
            return user.value;
          });
        const scenarios_ = formDataCreateRoleplay?.scenarios
          .map((scenario) => {
            if (scenario.name.length > 0)
              return scenariosDescriptions
                ? { name: scenario.name, description: scenario.description }
                : { name: scenario.name };
            return {};
          })
          .filter((scenario) => Object.keys(scenario).length > 0);

        const rating_criterial_ = formDataCreateRoleplay?.rating_criterials
          .map((criteria) => {
            if (criteria.name.length > 0) return { name: criteria.name };
            return {};
          })
          .filter((criteria) => Object.keys(criteria).length > 0);

        let data = {
          roleplay: {
            name: formDataCreateRoleplay.name,
            description: formDataCreateRoleplay.description,
          },
          sales_rep_uuid: sales_rep_uuid_,
          rating_criterials: rating_criterial_,
          deadline: formDataAssignRoleplay?.deadline,
        };

        if (scenarios_.length > 0 && Object.keys(scenarios_[0]).length > 0) {
          data = { ...data, scenarios: scenarios_ };
        }

        createCustomRoleplay(
          { roleplay: data },
          {
            onSuccess: () => {
              setFormDataAssignRoleplay({
                scenarios: [],
                deadline: DateTime.formatDate(new Date()) + ' 00:00:00',
                roleplay: firstRoleplay,
              });
              setFormDataCreateRoleplay({
                scenarios: [{ name: '', id: 1, description: '' }],
                rating_criterials: [{ name: '', id: 1 }],
                deadline: DateTime.formatDate(new Date()) + ' 00:00:00',
              });
              onClose();
            },
          }
        );
      }
    }
  };

  const onClose = () => {
    setScenariosList([]);
    setNameError(false);
    setDescriptionError(false);
    setCriteriaError(false);
    setScenariosErrors([]);
    reset();
    handleClose();
  };

  const scenarios_uuid = useMemo(
    () => scenariosList?.filter((sce) => sce?.isSelected).map((x) => x?.uuid),
    [scenariosList]
  );

  const itHasNoScenarios = useMemo(() => !scenariosList?.length, [scenariosList?.length]);

  const ai_persona_uuid = useMemo(() => {
    const persona = personas_ai?.find((p) => p?.isSelected);
    return persona?.uuid ?? '';
  }, [personas_ai]);

  const attitude_uuid = useMemo(() => {
    const item = attitudes?.find((att) => att?.isSelected);
    return item?.uuid ?? '';
  }, [attitudes]);

  const enableInteractive = useMemo(() => {
    if (isRep) {
      if (
        ai_persona_uuid !== '' &&
        attitude_uuid !== '' &&
        (scenariosList?.length > 0 ? scenarios_uuid?.length > 0 : true) &&
        role !== '' &&
        industry !== '' &&
        situation !== ''
      )
        return true;
      return newLoadingRep;
    } else {
      if (
        ai_persona_uuid !== '' &&
        attitude_uuid !== '' &&
        (scenariosList?.length > 0 ? scenarios_uuid?.length > 0 : true) &&
        role !== '' &&
        industry !== '' &&
        situation !== '' &&
        users?.length > 0
      )
        return true;
      return newLoadingAdmin;
    }
  }, [
    isRep,
    ai_persona_uuid,
    attitude_uuid,
    scenarios_uuid,
    role,
    industry,
    situation,
    users?.length,
    newLoadingAdmin,
    newLoadingRep,
    scenariosList?.length,
  ]);

  const onSuccess = () => {
    onClose();
  };

  const handleAssignRealPlay = () => {
    if (isRep) {
      const data = {
        roleplay_uuid: formDataAssignRoleplay?.roleplay.value,
        ai_persona_uuid,
        attitude_uuid,
        deadline: deadline,
        scenarios_uuid,
        interactive: true,
        role,
        industry,
        situation,
      };
      newSelfAssignRoleplay(
        { roleplay: data },
        {
          onSuccess,
        }
      );
    } else {
      if (formDataAssignRoleplay?.roleplay.value !== -1) {
        const users_uuid = (users ?? []).map((user) => user?.value);
        const data = {
          roleplay_uuid: formDataAssignRoleplay?.roleplay.value,
          scenarios_uuid,
          users_uuid,
          deadline,
          interactive: true,
          ai_persona_uuid,
          attitude_uuid,
          role,
          industry,
          situation,
        };
        newAssignRoleplay(
          { roleplay: data },
          {
            onSuccess,
          }
        );
      }
    }
  };

  const roleplay_name = useMemo(
    () => formDataAssignRoleplay?.roleplay?.label,
    [formDataAssignRoleplay?.roleplay]
  );

  const enabledSubmit = useMemo(
    () =>
      active && (roleplay_name === 'Objection Handling' || roleplay_name === 'Elevator Pitch')
        ? enableInteractive
        : enable,
    [active, roleplay_name, enableInteractive, enable]
  );

  const handleSubmit = () => {
    if (active && (roleplay_name === 'Objection Handling' || roleplay_name === 'Elevator Pitch')) {
      handleAssignRealPlay();
    } else {
      handleAssign();
    }
  };

  const isInteractive = useMemo(
    () => active && (roleplay_name === 'Objection Handling' || roleplay_name === 'Elevator Pitch'),
    [active, roleplay_name]
  );

  return {
    isRep,
    isLoading,
    isLoadingCoach,
    isLoadingAttitudes,
    enabledSubmit,
    handleSubmit,
    onClose,
    isInteractive,
    itHasNoScenarios,
  };
};

export default AssignRolePlayLogic;
